import { z } from 'zod'
import i18next from 'i18next'
import { errorParentIsRemoved, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type { Coassign_Employment_Insert_Input, Coassign_Employment_Set_Input } from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import { withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'
import { pick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'
import { isExist } from '@/lib/relation-graph'

const TABLE_NAME = 'employment' as const

const isUniqueKey = async ({
  id,
  user_id,
  start
}: {
  id: string
  user_id: string
  start: string | null
}): Promise<Boolean> => {
  const isExist = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom(TABLE_NAME)
      .select('id')
      .where('user_id', '=', user_id)
      .where('start', '=', start)
      .where('id', '!=', id)
      .execute()
  })
  if (isExist.length > 0) return false
  return true
}

export const schema = z
  .object({
    id: z.string().length(21),
    // contextual values
    user_id: z.string().length(21),
    // can modifies
    start: z
      .string()
      .regex(/^(19[8-9]\d|20\d\d)-\d{2}-01$/, {
        message: i18next.t('stages.labels.error.employment.invalidStartDate', { ns: 'translation' })
      })
      .nullable(),
    partner_id: z.string().length(21).refine(isExist('partner'), errorParentIsRemoved).nullish(),
    type: z.string().max(7),
    cost: z
      .number()
      .int({
        message: i18next.t('stages.labels.error.employment.costMustBeInteger', {
          ns: 'translation'
        })
      })
      .min(0, {
        message: i18next.t('stages.labels.error.employment.costNegative', { ns: 'translation' })
      })
      .max(9999999, {
        message: i18next.t('stages.labels.error.employment.costTooLarge', { ns: 'translation' })
      })
  })
  .refine(isUniqueKey, {
    message: i18next.t('stages.labels.error.employment.duplicateStartMonth', { ns: 'translation' }),
    path: ['start']
  })

export type Employment = z.infer<typeof schema>

export class EmploymentMutator extends Mutator<typeof schema> {
  static readonly stageableClassName = 'EmploymentMutator'
  schema = schema
  table = 'employment' as const

  static async load(
    id: string,
    context: Partial<Employment>
  ): Promise<Reactive<EmploymentMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(TABLE_NAME)
      .selectAll()
      .where('employment.id', '=', id)
      .executeTakeFirst()

    self.context = { ...context, ...{ id } }
    self.base = base
    return self
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    return [DataBase.updateTable(this.table).set(params).where('id', '=', id).compile()]
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const tenant_id = useUserStore().userInfo!.tenantId
    const params = { ...(await this.parsed)!, ...{ tenant_id } }
    return [DataBase.insertInto(this.table).values(params).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    if (this.base && this.base.start === null) throw new Error('Default Employment cannot delete')
    const id = this.context.id!
    return [DataBase.deleteFrom(this.table).where('id', '=', id).compile()]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const variables: Coassign_Employment_Insert_Input = { ...params, ...{ commit_id } }
    return [{ mutation: insertEmploymentMutationDocument, variables: { input: variables } }]
  }

  async asDeleteMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    if (this.base && this.base.start === null) throw new Error('Default Employment cannot delete')
    const id = this.context.id!
    const variables: Coassign_Employment_Set_Input = {
      commit_id: commit_id,
      deleted_at: formatISO(new Date())
    }
    return [{ mutation: updateEmploymentMutationDocument, variables: { id: id, input: variables } }]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const variables: Coassign_Employment_Set_Input = { ...params, ...{ commit_id } }
    return [{ mutation: updateEmploymentMutationDocument, variables: { id: id, input: variables } }]
  }
}

const insertEmploymentMutationDocument = graphql(/* GraphQL */ `
  mutation insertEmployment($input: coassign_employment_insert_input!) {
    insert_coassign_employment_one(object: $input) {
      created_at
    }
  }
`)

const updateEmploymentMutationDocument = graphql(/* GraphQL */ `
  mutation updateEmployment($id: String!, $input: coassign_employment_set_input!) {
    update_coassign_employment_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)
